import { hasOpenAiConsent, isOpenAiAdsEnabled } from './index'
import { hashOpenAiCustomer, OpenAiCustomer, OpenAiUserData } from './userData'

const PIXEL_SRC = 'https://bzrcdn.openai.com/sdk/oaiq.min.js'

type OpenAiEventName =
  | 'page_viewed'
  | 'contents_viewed'
  | 'items_added'
  | 'checkout_started'

type OpenAiContent = {
  id?: string
  name?: string
  content_type: 'product'
  quantity?: number
  amount?: number
  currency?: string
}

type OpenAiContentsData = {
  type: 'contents'
  amount?: number
  currency?: string
  contents?: OpenAiContent[]
}

type OpenAiPixelQueue = ((...args: unknown[]) => void) & { q?: unknown[] }

type OpenAiPixelWindow = Window & { oaiq?: OpenAiPixelQueue }

// Only digests live in memory. Never persist raw customer data for this pixel.
let currentUser: OpenAiUserData | undefined

export function clearOpenAiCustomer(): void {
  const hadUser = !!currentUser
  currentUser = undefined
  try {
    if (hadUser && typeof window !== 'undefined') {
      ;(window as OpenAiPixelWindow).oaiq?.('init', { user: {} })
    }
  } catch {
    // Identity cleanup must not interrupt logout or consent withdrawal.
  }
}

export function identifyOpenAiCustomer(customer: OpenAiCustomer): void {
  if (!getPixelId() || !hasOpenAiConsent()) return
  try {
    const user = hashOpenAiCustomer(customer)
    if (!Object.keys(user).length) return
    const sameId =
      !!user.external_id_sha256 &&
      user.external_id_sha256 === currentUser?.external_id_sha256
    const sameEmail =
      !!user.email_sha256 && user.email_sha256 === currentUser?.email_sha256
    const conflictingId =
      !!user.external_id_sha256 && !!currentUser?.external_id_sha256 && !sameId
    const conflictingEmail =
      !!user.email_sha256 && !!currentUser?.email_sha256 && !sameEmail
    // A different account/email must not inherit the previous person's details.
    currentUser =
      !conflictingId && !conflictingEmail && (sameId || sameEmail)
        ? { ...currentUser, ...user }
        : user
    const w = window as OpenAiPixelWindow
    if (!w.oaiq) syncOpenAiPixel()
    w.oaiq?.('init', { user: currentUser })
  } catch {
    // Hashing or SDK failures must not affect shopping.
    clearOpenAiCustomer()
  }
}

export function toMinorUnits(amount: unknown): number | undefined {
  const value = Number(amount)
  return Number.isFinite(value) && value > 0
    ? Math.round(value * 100)
    : undefined
}

// Item ids are variant SKUs, matching the backend's order_created, or the
// colourway product ID when no variant is selected yet.
export function toOpenAiContent({
  id,
  title,
  price,
  currency,
  quantity = 1
}: {
  id?: string | null
  title?: string | null
  price?: unknown
  currency?: string | null
  quantity?: number
}): OpenAiContent {
  const amount = toMinorUnits(price)
  return {
    ...(id && { id }),
    ...(title && { name: title }),
    content_type: 'product',
    quantity,
    ...(amount && { amount }),
    ...(currency && { currency })
  }
}

function getPixelId(): string | undefined {
  return process.env.OPENAI_PIXEL_ID || undefined
}

// OpenAI's loader snippet: queue calls on window.oaiq until the SDK loads.
function loadPixel(pixelId: string) {
  const w = window as OpenAiPixelWindow
  if (w.oaiq) return
  const queue: OpenAiPixelQueue = (...args: unknown[]) => {
    queue.q?.push(args)
  }
  queue.q = []
  w.oaiq = queue
  const script = document.createElement('script')
  script.async = true
  script.src = PIXEL_SRC
  document.head.appendChild(script)
  queue('init', { pixelId })
}

// The SDK is only loaded with consent. Withdrawing consent tells the pixel to
// stop measuring and remove its cookies.
export function syncOpenAiPixel(): void {
  const pixelId = getPixelId()
  if (!isOpenAiAdsEnabled() || !pixelId) return
  try {
    const w = window as OpenAiPixelWindow
    if (hasOpenAiConsent()) {
      loadPixel(pixelId)
      w.oaiq?.('consent', true)
    } else {
      clearOpenAiCustomer()
      w.oaiq?.('consent', false)
    }
  } catch {
    // Tracking must not affect browsing.
  }
}

export function trackOpenAiEvent(
  name: OpenAiEventName,
  data: OpenAiContentsData
): void {
  if (!getPixelId() || !hasOpenAiConsent()) return
  try {
    const w = window as OpenAiPixelWindow
    if (!w.oaiq) syncOpenAiPixel()
    w.oaiq?.('measure', name, data)
  } catch {
    // Tracking must not affect browsing.
  }
}

// page_viewed is for landing pages only: the home page, content pages and
// collections. Product pages send contents_viewed instead.
export function isOpenAiLandingPage(pathname: string): boolean {
  return pathname.startsWith('/pages') || pathname.startsWith('/collections')
}
